import { useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../services/firebase/config';

/**
 * Feature flags stored in Firestore at system/featureFlags.
 *
 * To add a new flag:
 * 1. Add the flag name to the FeatureFlags interface below
 * 2. Set a sensible default in DEFAULT_FLAGS
 * 3. Toggle in Firebase Console: system/featureFlags document
 *
 * Usage:
 *   const { flags, isEnabled } = useFeatureFlags();
 *   if (isEnabled('newDashboard')) { ... }
 */

export interface FeatureFlags {
  /** Screen Review — floating annotate-and-report button (kill-switch). */
  screenReview?: boolean;
  [key: string]: boolean | undefined;
}

// screenReview defaults OFF; seeded true via scripts/seed-screen-review-flag.js
const DEFAULT_FLAGS: FeatureFlags = {};

export function useFeatureFlags() {
  const [flags, setFlags] = useState<FeatureFlags>(DEFAULT_FLAGS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, 'system', 'featureFlags'),
      (snapshot) => {
        if (snapshot.exists()) {
          setFlags({ ...DEFAULT_FLAGS, ...snapshot.data() });
        } else {
          setFlags(DEFAULT_FLAGS);
        }
        setLoading(false);
      },
      (error) => {
        console.warn('Failed to load feature flags:', (error instanceof Error ? error.message : String(error)));
        setFlags(DEFAULT_FLAGS);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, []);

  const isEnabled = (flagName: string): boolean => {
    return flags[flagName] === true;
  };

  return { flags, loading, isEnabled };
}
